export interface Notification {
	id: string;
	message: string;
	title?: string;
	type?: "info" | "success" | "error";
	time?: string;
	createdAt: string;
	read?: boolean;
	[key: string]: unknown;
}

/**
 * Load notifications from API and merge with existing notifications
 */
export const loadNotificationsFromAPI = async (): Promise<void> => {
	try {
		const { getNotifications: fetchNotifications } = await import("@/api/services/notificationService");
		const apiNotifications = await fetchNotifications();
		
		// Get existing notifications from localStorage
		const existingNotifications = getNotifications();
		
		// Create a map of existing notification IDs to avoid duplicates
		const existingIds = new Set(existingNotifications.map(n => n.id));
		
		// Transform API notifications to match our Notification interface
		const newNotifications: Notification[] = apiNotifications.map((notif) => ({
			id: notif.id.toString(),
			message: notif.message,
			title: notif.title,
			type: (notif.type || "info") as "info" | "success" | "error",
			createdAt: notif.created_at || new Date().toISOString(),
			read: false, // API returns only unread notifications
		}));
		
		// Filter out duplicates and merge
		const uniqueNewNotifications = newNotifications.filter(n => !existingIds.has(n.id));
		
		if (uniqueNewNotifications.length > 0) {
			// Add new notifications to the beginning
			const allNotifications = [...uniqueNewNotifications, ...existingNotifications];
			saveNotifications(allNotifications);
			window.dispatchEvent(new CustomEvent("notifications-updated"));
		}
	} catch (error) {
		console.error("Error loading notifications from API:", error);
		// Don't throw - allow app to continue even if notifications fail
	}
};

const STORAGE_KEY = "notifications";

/**
 * Format date to "time ago" format
 */
export function formatTimeAgo(date: Date | string): string {
	const dateObj = typeof date === "string" ? new Date(date) : date;
	const now = new Date();
	const diffInSeconds = Math.floor((now.getTime() - dateObj.getTime()) / 1000);

	if (diffInSeconds < 60) {
		return "épp most";
	}

	const diffInMinutes = Math.floor(diffInSeconds / 60);
	if (diffInMinutes < 60) {
		return `${diffInMinutes} ${diffInMinutes === 1 ? "perce" : "perce"}`;
	}

	const diffInHours = Math.floor(diffInMinutes / 60);
	if (diffInHours < 24) {
		return `${diffInHours} ${diffInHours === 1 ? "órája" : "órája"}`;
	}

	const diffInDays = Math.floor(diffInHours / 24);
	if (diffInDays < 7) {
		return `${diffInDays} ${diffInDays === 1 ? "napja" : "napja"}`;
	}

	const diffInWeeks = Math.floor(diffInDays / 7);
	return `${diffInWeeks} ${diffInWeeks === 1 ? "hete" : "hete"}`;
}

/**
 * Get all notifications from localStorage
 */
export const getNotifications = (): Notification[] => {
	try {
		const stored = localStorage.getItem(STORAGE_KEY);
		if (!stored) return [];
		return JSON.parse(stored);
	} catch (error) {
		console.error("Error reading notifications from localStorage:", error);
		return [];
	}
};

/**
 * Save notifications to localStorage
 */
const saveNotifications = (notifications: Notification[]): void => {
	try {
		localStorage.setItem(STORAGE_KEY, JSON.stringify(notifications));
	} catch (error) {
		console.error("Error saving notifications to localStorage:", error);
	}
};

/**
 * Add a new notification
 */
export const addNotification = (notification: Notification): void => {
	const notifications = getNotifications();
	
	// Check if notification already exists (avoid duplicates)
	const exists = notifications.some((n) => n.id === notification.id);
	if (exists) {
		return;
	}

	const newNotification: Notification = {
		...notification,
		read: false,
		// Don't store time - calculate it dynamically when rendering
	};

	notifications.unshift(newNotification);
	saveNotifications(notifications);
	
	// Dispatch custom event to notify components
	window.dispatchEvent(new CustomEvent("notifications-updated"));
};

/**
 * Mark a notification as read
 */
export const markAsRead = async (id: string): Promise<void> => {
	const notifications = getNotifications();
	const notification = notifications.find((n) => n.id === id);
	
	// Update local state immediately
	const updated = notifications.map((n) =>
		n.id === id ? { ...n, read: true } : n,
	);
	saveNotifications(updated);
	window.dispatchEvent(new CustomEvent("notifications-updated"));
	
	// Call API to mark as read on backend
	if (notification) {
		try {
			const notificationId = parseInt(id, 10);
			if (!isNaN(notificationId)) {
				const { markNotificationAsRead } = await import("@/api/services/notificationService");
				await markNotificationAsRead(notificationId);
			}
		} catch (error) {
			console.error("Error marking notification as read on backend:", error);
			// Revert local state if API call fails
			const reverted = notifications.map((n) =>
				n.id === id ? { ...n, read: false } : n,
			);
			saveNotifications(reverted);
			window.dispatchEvent(new CustomEvent("notifications-updated"));
		}
	}
};

/**
 * Mark all notifications as read and remove them from local storage
 */
export const markAllAsRead = async (): Promise<void> => {
	const notifications = getNotifications();
	const unreadNotifications = notifications.filter((n) => !n.read);
	
	// Remove all notifications from local storage immediately
	clearAllNotifications();
	
	// Call API to mark all as read on backend
	if (unreadNotifications.length > 0) {
		try {
			const { markNotificationAsRead } = await import("@/api/services/notificationService");
			// Mark each unread notification as read
			await Promise.all(
				unreadNotifications.map(async (notif) => {
					const notificationId = parseInt(notif.id, 10);
					if (!isNaN(notificationId)) {
						try {
							await markNotificationAsRead(notificationId);
						} catch (error) {
							console.error(`Error marking notification ${notif.id} as read:`, error);
						}
					}
				})
			);
		} catch (error) {
			console.error("Error marking all notifications as read on backend:", error);
			// Note: We don't revert here as it's a bulk operation
		}
	}
};

/**
 * Clear all notifications
 */
export const clearAllNotifications = (): void => {
	saveNotifications([]);
	window.dispatchEvent(new CustomEvent("notifications-updated"));
};

/**
 * Remove a notification
 */
export const removeNotification = (id: string): void => {
	const notifications = getNotifications();
	const updated = notifications.filter((n) => n.id !== id);
	saveNotifications(updated);
	window.dispatchEvent(new CustomEvent("notifications-updated"));
};

/**
 * Mark a notification as read on backend and remove it from local storage
 */
export const markAsReadAndRemove = async (id: string): Promise<void> => {
	const notifications = getNotifications();
	const notification = notifications.find((n) => n.id === id);
	
	// Remove from local storage immediately
	removeNotification(id);
	
	// Call API to mark as read on backend
	if (notification) {
		try {
			const notificationId = parseInt(id, 10);
			if (!isNaN(notificationId)) {
				const { markNotificationAsRead } = await import("@/api/services/notificationService");
				await markNotificationAsRead(notificationId);
			}
		} catch (error) {
			console.error("Error marking notification as read on backend:", error);
			// If API call fails, we don't restore the notification since user already clicked it
		}
	}
};

/**
 * Get unread count
 */
export const getUnreadCount = (): number => {
	const notifications = getNotifications();
	return notifications.filter((n) => !n.read).length;
};
